import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem, styled,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import Select, {SelectChangeEvent} from '@mui/material/Select';
import React, {useState} from "react";
import View from "../../components/View";
import Page from "../../components/Page";

interface Recipe {
    gas: string;
    blendingMethod: string
    nitrox: string
    pO2Extra: number
    pO2: string
    pHe: string
    pN2: string
    wantHE: number
    wantO2: number
    wantPressure: number
}

const Trimix = () => {

    const [targetO2, setTargetO2] = useState(21);
    const [targetHe, setTargetHe] = useState(30);
    const [targetPressure, setTargetPressure] = useState(232);
    const [blendingMethod, setBlendingMethod] = useState('partial');
    const [recipe, setRecipe] = useState<Recipe>({
        blendingMethod: "",
        gas: "",
        nitrox: "",
        pHe: "",
        pN2: "",
        pO2: "",
        pO2Extra: 0,
        wantHE: 0,
        wantO2: 0,
        wantPressure: 0
    })
    const [alert, setAlert] = useState(false)
    const [alertDescription, setAlertDescription] = useState('')

    const changeTargetO2 = (event: SelectChangeEvent) => {
        const value = parseFloat(event.target.value);
        setTargetO2(value)
    }

    const changeTargetHe = (event: SelectChangeEvent) => {
        const value = parseFloat(event.target.value);
        setTargetHe(value)
    }

    const changeTargetPressure = (event: SelectChangeEvent) => {
        const value = parseInt(event.target.value);
        setTargetPressure(value)
    }

    const changeAlert = (description: string) => {
        setAlertDescription(description)
        setAlert(true);
    }

    const clearAlert = (): void => {
        setAlert(false)
        setAlertDescription('')
    }

    const [open, setOpen] = React.useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    // @ts-ignore
    const changeBlendingMethod = (event) => {
        setBlendingMethod(event.target.value)
    }

    const calcFO2 = (pO2: number, pN2: number): number => {
        return pO2 / (pO2 + pN2);
    }

    const calcPartialFill = (fO2: number, pO2: number, pN2: number): number => {
        return ((fO2 - 0.21) / 0.79) * (pO2 + pN2)
    }

    const calc = (): void => {
        calcMix(targetO2, targetHe, targetPressure);
    }

    const calcMix = (wantO2: number, wantHE: number, wantPressure: number): void => {
        let fO2 = wantO2 / 100;
        let fHE = wantHE / 100;
        let fN2 = 1 - fHE - fO2;
        let pO2 = fO2 * wantPressure;
        let pHE = fHE * wantPressure;
        let pN2 = fN2 * wantPressure;

        let nitrox = calcFO2(pO2, pN2);
        let pO2Extra = calcPartialFill(nitrox, pO2, pN2);

        let recipe = {
            gas: 'TX ' + targetO2 + '/' + targetHe,
            blendingMethod: blendingMethod,
            nitrox: (nitrox * 100).toFixed(1),
            pO2Extra: pO2Extra,
            pO2: pO2.toFixed(1),
            pHe: pHE.toFixed(1),
            pN2: pN2.toFixed(1),
            wantO2: wantO2,
            wantHE: wantHE,
            wantPressure: wantPressure
        };

        setRecipe(recipe)
        setOpen(true)
    }

    // @ts-ignore
    return (
        <Page>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    <Typography variant="h4" align="center" mt={2}>{recipe.gas}</Typography>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        <Typography variant="h6" align={"center"}>Blending Method: {recipe.blendingMethod}</Typography>
                        <Grid container rowSpacing={1} columnSpacing={2.75} sx={{mb: 2, mt: 1}}>
                            <Grid item xs={6}></Grid>
                            <Grid item xs={3}><Typography fontWeight={800}>Have</Typography></Grid>
                            <Grid item xs={3}><Typography fontWeight={800}>Want</Typography></Grid>
                            <Grid item xs={6}>Oxygen</Grid>
                            <Grid item xs={3}><Typography align={"center"}>21 %</Typography></Grid>
                            <Grid item xs={3}><Typography align={"center"}>{targetO2} %</Typography></Grid>
                            <Grid item xs={6}>Helium</Grid>
                            <Grid item xs={3}><Typography align={"center"}>0 %</Typography></Grid>
                            <Grid item xs={3}><Typography align={"center"}>{targetHe} %</Typography></Grid>
                            <Grid item xs={6}>Pressure</Grid>
                            <Grid item xs={3}><Typography align={"center"}>0</Typography></Grid>
                            <Grid item xs={3}><Typography align={"center"}>{recipe.wantPressure}</Typography></Grid>
                        </Grid>
                        <Typography mt={4} mb={1} variant="h6" align={"center"}>Blending instructions</Typography>
                        <Grid container rowSpacing={1} columnSpacing={2.75} sx={{mb: 1}}>
                            <Grid item xs={6}><Typography fontWeight={800}>Action</Typography></Grid>
                            <Grid item xs={3}><Typography fontWeight={800} align={"right"}>+/-</Typography></Grid>
                            <Grid item xs={3}><Typography fontWeight={800} align={"right"}>To</Typography></Grid>
                        </Grid>
                        <Grid container rowSpacing={1} columnSpacing={2.75}>
                            <Grid item xs={6}>Add Helium</Grid>
                            <Grid item xs={3}>
                                <Typography
                                    align={"right"}>{Number(recipe.pHe).toFixed(1)}</Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography
                                    align={"right"}>{recipe.pHe}</Typography>
                            </Grid>

                            {blendingMethod === 'partial' ? (
                                <>
                                    <Grid item xs={6}>Add Oxygen</Grid>
                                    <Grid item xs={3}>
                                        <Typography
                                            align={"right"}>{Number(recipe.pO2Extra).toFixed(1)}</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography
                                            align={"right"}>{Number(Number(recipe.pHe) + Number(recipe.pO2Extra)).toFixed(1)}</Typography>
                                    </Grid>

                                    <Grid item xs={6}>Top up with air</Grid>
                                    <Grid item xs={3}><Typography
                                        align={"right"}>{Number(recipe.wantPressure - Number(recipe.pHe) - recipe.pO2Extra).toFixed(1)}</Typography></Grid>
                                    <Grid item xs={3}><Typography
                                        align={"right"}>{Number(recipe.wantPressure).toFixed(1)}</Typography></Grid>
                                </>
                            ) : (
                                <>
                                    <Grid item xs={6}>{"Add EAN" + recipe.nitrox}</Grid>
                                    <Grid item xs={3}>
                                        {Number(recipe.wantPressure - Number(recipe.pHe)).toFixed(1)}
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography
                                            align={"right"}>{Number(recipe.wantPressure).toFixed(1)}</Typography>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} autoFocus>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <View>
                <Typography variant="h6" sx={{mt: 1, mb: 1}}>TRIMIX</Typography>
            </View>
            <View>
                {alert ? <Alert severity="error">{alertDescription}</Alert> : ''}
                <Box sx={{marginTop: 2}}>
                    <ToggleButtonGroup
                        color="primary"
                        value={blendingMethod}
                        exclusive
                        onChange={changeBlendingMethod}
                        aria-label="Platform"
                    >
                        <ToggleButton value="partial">Partial</ToggleButton>
                        <ToggleButton value="continuous">Continuous</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <Box sx={{marginTop: 5}}>
                    <Typography variant="h6" sx={{mt: 1, mb: 1}}>Target</Typography>
                    <FormControl variant="standard" sx={{m: 2, minWidth: 90}}>
                        <InputLabel id="targetO2">Oxygen</InputLabel>
                        <Select
                            labelId="targetO2"
                            id="targetO2"
                            value={targetO2.toString()}
                            label="targetO2"
                            onChange={changeTargetO2}
                        >
                            {[...Array(81)].map((x, i: number) => (
                                <MenuItem key={'tmix' + x} value={20 + i}>{20 + i}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl variant="standard" sx={{m: 2, minWidth: 90}}>
                        <InputLabel id="targetHe">Helium</InputLabel>
                        <Select
                            labelId="targetHe"
                            id="targetHe"
                            value={targetHe.toString()}
                            label="targetHe"
                            onChange={changeTargetHe}
                        >
                            {[...Array(99)].map((x, i: number) => (
                                <MenuItem key={'hemix' + x} value={0 + i}>{0 + i}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl variant="standard" sx={{m: 2, minWidth: 90}}>
                        <InputLabel id="targetPressure">Target Pressure</InputLabel>
                        <Select
                            labelId="targetPressure"
                            id="targetPressure"
                            value={targetPressure.toString()}
                            label="targetPressure"
                            onChange={changeTargetPressure}
                        >
                            {[...Array(300)].map((x, i: number) => (
                                <MenuItem key={'tp' + x} value={1 + i}>{1 + i}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{marginTop: 5}}>
                    <Button variant="contained" onClick={calc}>Calculate</Button>
                </Box>
            </View>
        </Page>
    );
}

export default Trimix;